<template>
  <v-container>
    <v-row>
      <v-col cols="12" class="d-flex justify-space-between align-center">
        <h1 class="text-h4">{{ $t('users.title') }}</h1>
        <v-btn color="primary" prepend-icon="mdi-account-plus" @click="openCreateDialog">
          {{ $t('users.create') }}
        </v-btn>
      </v-col>
    </v-row>

    <v-row>
      <v-col cols="12">
        <v-card>
          <v-data-table
            :headers="headers"
            :items="users"
            :loading="loading"
            class="elevation-1"
          >
            <template v-slot:item.role="{ item }">
              <v-chip :color="getRoleColor(item.role)" size="small" label>
                {{ roleLabel(item.role) }}
              </v-chip>
            </template>
            <template v-slot:item.createdAt="{ item }">
              {{ formatDate(item.createdAt) }}
            </template>
            <template v-slot:item.actions="{ item }">
              <v-icon size="small" class="mr-2" @click="editUser(item)">
                mdi-pencil
              </v-icon>
              <v-icon size="small" color="error" @click="confirmDelete(item)" :disabled="item.role === 'superadmin'">
                mdi-delete
              </v-icon>
            </template>
          </v-data-table>
        </v-card>
      </v-col>
    </v-row>

    <!-- Create/Edit Dialog -->
    <v-dialog v-model="dialog" max-width="500px">
      <v-card>
        <v-card-title>
          <span class="text-h5">{{ isEditing ? $t('users.edit') : $t('users.create') }}</span>
        </v-card-title>
        <v-card-text>
          <v-form ref="form" v-model="valid">
            <v-text-field
              v-model="editedUser.username"
              :label="$t('users.username')"
              :disabled="isEditing"
              required
            ></v-text-field>
            <v-text-field
              v-model="editedUser.password"
              :label="$t('users.password')"
              type="password"
              required
            ></v-text-field>
            <v-select
              v-model="editedUser.role"
              :items="roleItems"
              item-title="title"
              item-value="value"
              :label="$t('users.role')"
              :hint="$t('users.roleHint')"
              persistent-hint
              required
            ></v-select>
          </v-form>
        </v-card-text>
        <v-card-actions>
          <v-spacer></v-spacer>
          <v-btn color="grey" variant="text" @click="closeDialog">{{ $t('common.cancel') }}</v-btn>
          <v-btn color="primary" variant="text" @click="saveUser" :disabled="!valid">{{ $t('common.save') }}</v-btn>
        </v-card-actions>
      </v-card>
    </v-dialog>

    <!-- Delete Confirmation -->
    <v-dialog v-model="deleteDialog" max-width="400px">
      <v-card>
        <v-card-title class="text-h5">{{ $t('users.deleteConfirmTitle') }}</v-card-title>
        <v-card-text>{{ $t('users.deleteConfirmMessage', { name: userToDelete?.username }) }}</v-card-text>
        <v-card-actions>
          <v-spacer></v-spacer>
          <v-btn color="grey" variant="text" @click="deleteDialog = false">{{ $t('common.cancel') }}</v-btn>
          <v-btn color="error" variant="text" @click="deleteUser">{{ $t('common.delete') }}</v-btn>
        </v-card-actions>
      </v-card>
    </v-dialog>
  </v-container>
</template>

<script setup>
import { ref, computed, onMounted } from 'vue'
import { apiCall } from '../utils/api.js'
import { useAuthStore } from '../stores/auth'
import { useProjectStore } from '../stores/projects.js'
import { useI18n } from 'vue-i18n'

const { t } = useI18n()
const authStore = useAuthStore()
const projectStore = useProjectStore()

const users = ref([])
const loading = ref(false)
const dialog = ref(false)
const deleteDialog = ref(false)
const valid = ref(false)
const isEditing = ref(false)
const userToDelete = ref(null)

const editedUser = ref({
  username: '',
  password: '',
  role: 'user'
})

/**
 * Profili assegnabili: `admin`, che opera tutte le fasi, più quelli dichiarati in cfg.json. La
 * lista non è più fissa perché dal profilo dipende quali fasi del processo l'utente può lavorare:
 * un valore che non esiste in cfg.json lascerebbe l'utente senza nessuna fase. `superadmin` non
 * compare: arriva dalla configurazione del server, non dal database. Il Backend riapplica la
 * stessa regola su create e update.
 */
const roleItems = computed(() => [
  { title: t('users.roleAdmin'), value: 'admin' },
  ...projectStore.workflowRoles.map(r => ({ title: `${r.label} (${r.id})`, value: r.id }))
])

const roleLabel = (id) => roleItems.value.find(r => r.value === id)?.title || id

const headers = [
  { title: t('users.username'), key: 'username' },
  { title: t('users.role'), key: 'role' },
  { title: t('users.createdAt'), key: 'createdAt' },
  { title: t('common.actions'), key: 'actions', sortable: false }
]

const loadUsers = async () => {
  loading.value = true
  try {
    const response = await apiCall('/api/users')
    users.value = response
  } catch (error) {
    console.error('Error loading users:', error)
  } finally {
    loading.value = false
  }
}

const openCreateDialog = () => {
  isEditing.value = false
  // Primo profilo configurato, non un id inventato: cosi' il nuovo utente nasce con delle fasi
  editedUser.value = {
    username: '',
    password: '',
    role: projectStore.workflowRoles[0]?.id || 'admin'
  }
  dialog.value = true
}

const editUser = (user) => {
  isEditing.value = true
  editedUser.value = { ...user, password: '' }
  dialog.value = true
}

const closeDialog = () => {
  dialog.value = false
}

const saveUser = async () => {
  try {
    if (isEditing.value) {
      await apiCall(`/api/users/${editedUser.value.username}`, {
        method: 'PUT',
        body: JSON.stringify(editedUser.value)
      })
    } else {
      await apiCall('/api/users', {
        method: 'POST',
        body: JSON.stringify(editedUser.value)
      })
    }
    dialog.value = false
    loadUsers()
  } catch (error) {
    console.error('Error saving user:', error)
    alert(error.response?.data?.message || error.message || 'Error saving user')
  }
}

const confirmDelete = (user) => {
  userToDelete.value = user
  deleteDialog.value = true
}

const deleteUser = async () => {
  try {
    await apiCall(`/api/users/${userToDelete.value.username}`, {
      method: 'DELETE'
    })
    deleteDialog.value = false
    loadUsers()
  } catch (error) {
    console.error('Error deleting user:', error)
  }
}

// Colore stabile per profilo: i due riservati hanno il loro, gli altri ruotano su una tavolozza
// fissa in base alla posizione in cfg.json, cosi' lo stesso profilo ha sempre lo stesso colore.
const ROLE_COLORS = ['teal', 'indigo', 'orange', 'cyan', 'deep-purple', 'brown']

const getRoleColor = (role) => {
  if (role === 'superadmin') return 'purple'
  if (role === 'admin') return 'primary'

  const index = projectStore.workflowRoles.findIndex(r => r.id === role)
  return index === -1 ? 'grey' : ROLE_COLORS[index % ROLE_COLORS.length]
}

const formatDate = (date) => {
  if (!date) return '-'
  return new Date(date).toLocaleDateString()
}

onMounted(async () => {
  // I profili vivono in cfg.json: senza configurazioni caricate il selettore sarebbe vuoto
  if (!projectStore.configurations?.config) {
    await projectStore.loadConfigurations()
  }
  await loadUsers()
})
</script>
